/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Clock, Tag } from "lucide-react";
import { POSTS } from "@/blog/posts";
import { setPageSeo } from "@/blog/seo";
import Ad from "@/blog/Ad";

const SITE = "https://growyourpresence.biz";

const BlogIndex = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    setPageSeo({
      title: "Blog — Grow Your Presence Online Daily",
      description:
        "Tactical guides on Answer Engine Optimization (AEO), Generative Engine Optimization (GEO) and ranking on AI search engines like ChatGPT, Perplexity and Google AI Overviews.",
      canonical: `${SITE}/blog`,
      ogImage: POSTS[0]?.cover,
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Blog",
          url: `${SITE}/blog`,
          isPartOf: { "@id": `${SITE}/#website` },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
          ],
        },
      ],
    });
  }, []);

  return (
    <section className="relative pt-32 pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-40 -top-20 h-[420px] w-[420px] rounded-full bg-[#FF512F] opacity-25 blur-[140px]" />
        <div className="absolute right-[-10%] top-[10%] h-[360px] w-[360px] rounded-full bg-[#F09819] opacity-20 blur-[140px]" />
      </div>

      <div className="mx-auto max-w-5xl px-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-[#F09819]">
          The Growth Journal
        </div>
        <h1 className="mt-6 font-display text-4xl font-black tracking-tight sm:text-6xl">
          Tactical guides on{" "}
          <span className="text-gradient-flame">ranking in AI search.</span>
        </h1>
        <p className="mt-5 max-w-2xl text-white/65">
          Field notes on AEO, GEO and the schemas, signals and content patterns AI engines like
          ChatGPT, Perplexity and Google's AI Overviews actually quote.
        </p>

        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {POSTS.map((p, i) => (
            <React.Fragment key={p.slug}>
              <motion.article
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`blog-card-${p.slug}`}
                className="glass group overflow-hidden rounded-3xl"
              >
              <Link to={`/blog/${p.slug}`} className="block">
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.coverAlt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050508] via-transparent to-transparent" />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-4 text-xs text-white/45">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(p.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> {p.readMinutes} min read
                    </span>
                  </div>
                  <h2 className="mt-3 font-display text-2xl font-bold leading-tight group-hover:text-gradient-flame">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">
                    {p.description}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.tags.slice(0, 3).map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/65"
                      >
                        <Tag className="h-3 w-3" />
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#F09819]">
                    Read the guide
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </motion.article>
              {i === 3 && (
                <div className="sm:col-span-2">
                  <Ad slot="blog-index-inline" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogIndex;
